import { onMounted, onBeforeUnmount, ref, nextTick, Ref } from 'vue'
import {
  isHubspotContentReady,
  monitorHubspotHydration,
  resetHubspotLoader,
} from '@/helpers/hubspotEmbed'

type UseHubspotOptions = {
  maxRetries?: number
  timeout?: number
  retryDelay?: number
}

type LoadEmbedOptions = {
  suppressLoader?: boolean
}

export function useHubspotEmbed(
  containerRef: Ref<HTMLElement | null>,
  options: UseHubspotOptions = {}
) {
  const isLoading = ref(true)
  const hasError = ref(false)
  const errorMessage = ref('')
  const showFallback = ref(true)
  const attempts = ref(0)

  const maxRetries = options.maxRetries ?? 2
  const timeout = options.timeout
  const retryDelay = options.retryDelay ?? 500

  let currentMonitorCleanup: (() => void) | null = null
  let retryTimer: ReturnType<typeof setTimeout> | null = null
  let postLoadObserver: MutationObserver | null = null
  let disappearTimer: ReturnType<typeof setTimeout> | null = null
  let loadRunId = 0
  let isActive = true
  let isMounted = false
  let loadEmbed: (loadOptions?: LoadEmbedOptions) => Promise<void>

  const clearRetryTimer = () => {
    if (retryTimer) {
      clearTimeout(retryTimer)
      retryTimer = null
    }
  }

  const cleanupMonitor = () => {
    if (currentMonitorCleanup) {
      currentMonitorCleanup()
      currentMonitorCleanup = null
    }
  }

  const stopPostLoadMonitor = () => {
    if (postLoadObserver) {
      postLoadObserver.disconnect()
      postLoadObserver = null
    }
    if (disappearTimer) {
      clearTimeout(disappearTimer)
      disappearTimer = null
    }
  }

  const startPostLoadMonitor = (container: HTMLElement) => {
    stopPostLoadMonitor()

    postLoadObserver = new MutationObserver(() => {
      if (isHubspotContentReady(container)) {
        if (disappearTimer) {
          clearTimeout(disappearTimer)
          disappearTimer = null
        }
        return
      }

      if (!disappearTimer) {
        disappearTimer = setTimeout(() => {
          disappearTimer = null
          if (!isMounted || !isActive) return
          if (isHubspotContentReady(containerRef.value)) return

          stopPostLoadMonitor()
          attempts.value = 0
          showFallback.value = true
          loadEmbed()
        }, 500)
      }
    })

    postLoadObserver.observe(container, { childList: true, subtree: true })
  }

  const stopLoading = (clearFallback = true) => {
    clearRetryTimer()
    cleanupMonitor()
    isLoading.value = false
    hasError.value = false
    errorMessage.value = ''
    if (clearFallback) {
      showFallback.value = true
    }
  }

  const scheduleRetry = () => {
    if (!isMounted || !isActive || attempts.value > maxRetries) return

    clearRetryTimer()
    retryTimer = setTimeout(() => {
      retryTimer = null
      loadEmbed({ suppressLoader: true })
    }, retryDelay)
  }

  loadEmbed = async (loadOptions: LoadEmbedOptions = {}) => {
    if (!isMounted || !isActive) return
    const suppressLoader = loadOptions.suppressLoader ?? false
    const runId = ++loadRunId

    clearRetryTimer()
    cleanupMonitor()
    stopPostLoadMonitor()

    if (!suppressLoader) {
      isLoading.value = true
    }
    hasError.value = false
    errorMessage.value = ''
    attempts.value += 1

    try {
      await resetHubspotLoader()
      await nextTick()
    } catch {
      if (!isMounted || runId !== loadRunId) return
      showFallback.value = true
      isLoading.value = false
      scheduleRetry()
      return
    }

    if (!isMounted || runId !== loadRunId) return

    if (!containerRef?.value) {
      hasError.value = true
      errorMessage.value = 'HubSpot container is not available.'
      isLoading.value = false
      return
    }

    const { promise, cleanup } = monitorHubspotHydration(containerRef.value, { timeout })
    currentMonitorCleanup = cleanup

    promise
      .then(() => {
        if (!isMounted || runId !== loadRunId) return
        isLoading.value = false
        showFallback.value = false
        hasError.value = false
        errorMessage.value = ''
        if (containerRef.value) {
          startPostLoadMonitor(containerRef.value)
        }
      })
      .catch(() => {
        if (!isMounted || runId !== loadRunId) return

        const container = containerRef?.value
        if (isHubspotContentReady(container)) {
          isLoading.value = false
          showFallback.value = false
          hasError.value = false
          errorMessage.value = ''
          return
        }

        // Keep UX stable with fallback while retries continue in background.
        isLoading.value = false
        showFallback.value = true
        hasError.value = false
        errorMessage.value = ''
        scheduleRetry()
      })
  }

  onMounted(() => {
    isMounted = true
    if (isActive) {
      loadEmbed()
    }
  })

  onBeforeUnmount(() => {
    isMounted = false
    stopLoading()
    stopPostLoadMonitor()
  })

  return {
    isLoading,
    showFallback,
    hasError,
    errorMessage,
    setActive: (active: boolean) => {
      if (isActive === active) return
      isActive = active
      if (!isMounted) return

      if (!isActive) {
        stopLoading(false)
        return
      }

      const container = containerRef?.value
      if (isHubspotContentReady(container)) {
        isLoading.value = false
        hasError.value = false
        errorMessage.value = ''
        return
      }

      attempts.value = 0
      loadEmbed()
    },
    reload: () => {
      attempts.value = 0
      stopLoading()
      loadEmbed()
    },
  }
}
